import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'

import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { ConnectAccountButton } from './ConnectAccountButton'
import { DownloadButton } from './DownloadButton'

import Styles from './PostActions.module.scss'
import { Button } from 'blaze-ui/Button'
import { PostPreviewScreens } from 'components/PostPreviewModalV2/PostPreviewStore'
import { usePostPreviewStore } from 'components/PostPreviewModalV2/PostPreviewStoreProvider'
import { usePostActionService, PostAction } from 'components/PostPreviewModalV2/hooks/usePostActionService'
import { MobileButton } from 'components/MobileButton/MobileButton'
import { Alarm2, Save2 } from 'blaze-ui/icons'

import { useModals } from 'almanac-ui/Modal'
import { PostNowConfirmationModal } from 'components/PostStatusDropdown/PostNowConfirmationModal'
import { UnconnectedChannelsModal } from '../UnconnectedChannelsModal/UnconnectedChannelsModal'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'
import { uniq } from 'lodash'
import { DesignChannel } from 'components/NewFileModal/Types'

export const PostActions = observer(() => {
  const store = usePostPreviewStore()
  const { data: kit } = useBrandKit(store.selectedBrandKitId)
  const { run } = usePostActionService()
  const { openModal, closeModal } = useModals()
  const { previewIsStale } = usePostPreviewContext()

  const provider = store.channel && providerFromChannelType[store.channel]
  const hasIntegration = provider && kit?.brand_kit.external_integrations.some((i) => i.provider === provider)
  const selectedAccounts = store.selectedAccounts.map((a) => a.provider)
  const unconnected = [...store.selectedChannels].filter(
    (p) => !kit?.brand_kit.external_integrations.some((i) => i.provider === p),
  )

  const guardUnconnected = (next: () => void) => {
    if (unconnected.length === 0) {
      next()
      return
    }

    openModal(
      UnconnectedChannelsModal,
      {
        onConfirm: () => next(),
        onBack: () => closeModal(),
      },
      { newStack: true },
    )
  }

  const doNow = () => {
    if (unconnected.length > 0) {
      openModal(
        UnconnectedChannelsModal,
        {
          onConfirm: () => {
            run(PostAction.PublishNow)
          },
          onBack: () => closeModal(),
        },
        { newStack: true },
      )
      return
    }

    // For YouTube, skip confirmation modal and go directly to YouTube screen
    if (store.channel === DesignChannel.YoutubeShort) {
      run(PostAction.PublishNow)
      return
    }

    openModal(
      PostNowConfirmationModal,
      {
        providers: uniq(selectedAccounts),
        provider: provider ?? 'this channel',
        closeOnResult: false,
        onConfirm: async () => {
          await run(PostAction.PublishNow)
        },
      },
      { newStack: true },
    )
  }

  const saveDraft = useCallback(() => run(PostAction.Draft), [run])

  const openAuto = () => guardUnconnected(() => store.setScreen(PostPreviewScreens.AUTOPOST_CALENDAR_SCREEN))

  const openRemind = () => store.setScreen(PostPreviewScreens.REMINDER_CALENDAR_SCREEN)

  if (provider && !hasIntegration && store.selectedAccounts.length === 0) {
    return (
      <div className={Styles.root}>
        <ConnectAccountButton provider={provider} />
        <div className={Styles.secondaryActions}>
          <MobileButton size="sm" variant="tertiary" onPress={saveDraft} icon={Save2} isDisabled={previewIsStale}>
            Save Draft
          </MobileButton>
          <MobileButton size="sm" variant="tertiary" onPress={openRemind} icon={Alarm2} isDisabled={previewIsStale}>
            Set Reminder
          </MobileButton>
          <DownloadButton />
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.root}>
      <Button size="lg" variant="primary" fullWidth onPress={doNow} isDisabled={previewIsStale}>
        Post Now
      </Button>

      <Button size="lg" variant="tertiary" fullWidth onPress={openAuto} isDisabled={previewIsStale}>
        Schedule Posts
      </Button>

      <div className={Styles.secondaryActions}>
        <MobileButton variant="tertiary" icon={Save2} size="sm" onPress={saveDraft} isDisabled={previewIsStale}>
          Save Drafts
        </MobileButton>
        <MobileButton variant="tertiary" icon={Alarm2} size="sm" onPress={openRemind} isDisabled={previewIsStale}>
          Set Reminder
        </MobileButton>
        <DownloadButton />
      </div>
    </div>
  )
})
